import {DBObjects} from '@/store/store_types';

import type {FileClient, StoredFileMeta, StoredFileMetaWithDurationAndWaveformData, DirectoryUploadCallbacks} from '../files_module';
import springboard from 'springboard';

import JSZip from 'jszip';

// turn this into an actual module that is shared between server and client
// return a url for upload and a url for download to the FileClient
// this module isn't even browser specific. it's just server-specific

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        RemoteFileClient: RemoteFileClientModuleReturnValue;
    }
}

export type RemoteFileClientModuleReturnValue = {
    client: () => FileClient;
    createUploadUrl: (args: {workspaceId: string, fileName: string, contentType: string}) => Promise<{error: string} | {url: string, filePath: string}>;
    createMultipartUpload: (args: {workspaceId: string, fileName: string, contentType: string}) => Promise<{error: string} | {uploadId: string, filePath: string}>;
    createMultipartUploadPartUrl: (args: {filePath: string, uploadId: string, partNumber: number, contentType: string}) => Promise<{error: string} | {url: string}>;
    completeMultipartUpload: (args: {filePath: string, uploadId: string, parts: Array<{ETag: string; PartNumber: number}>}) => Promise<{error?: string, success?: boolean}>;
    abortMultipartUpload: (args: {filePath: string, uploadId: string}) => Promise<{error?: string, success?: boolean}>;
};



import {generatePeaksFromArrayBuffer} from '@/utils/generate_waveform_data';
import {isAudioFile} from '@/utils/file_utils';
import {browserComputeSha1} from '@/utils/browser_hash_file';

export default (sb: typeof springboard) => sb.registerModule('RemoteFileClient', {rpcMode: 'remote'}, async (moduleAPI): Promise<RemoteFileClientModuleReturnValue> => {
    const actions = moduleAPI.createActions({
        createUploadUrl: async (args: {workspaceId: string, fileName: string, contentType: string}) => {
            
        },
        getFileSrc: async (args: {fileId: string}) => { // TODO: springboard should expose "hono context" in second arg
            
        },
        createMultipartUpload: async (args: {workspaceId: string, fileName: string, contentType: string}) => {
            
        },
        createMultipartUploadPartUrl: async (args: {filePath: string, uploadId: string, partNumber: number, contentType: string}) => {
            
        },
        completeMultipartUpload: async (args: {filePath: string, uploadId: string, parts: Array<{ETag: string; PartNumber: number}>}) => {
            
        },
        abortMultipartUpload: async (args: {filePath: string, uploadId: string}) => {
            
        },
    })

    const fileClient: FileClient = {
        getFileSrc: async (file: DBObjects['File']) => {
            console.log('[FileClient.getFileSrc] Called with file:', {
                id: file.id,
                name: file.name,
                relative_path: file.relative_path,
            });

            console.log('[FileClient.getFileSrc] Calling actions.getFileSrc...');
            const response = await actions.getFileSrc({fileId: file.id});

            console.log('[FileClient.getFileSrc] Response:', response);

            if ('error' in response) {
                console.error('[FileClient.getFileSrc] Error in response:', response.error);
                return '';
            }

            console.log('[FileClient.getFileSrc] Returning src:', response.src.substring(0, 100) + '...');
            return response.src;
        },
        chooseFileAndUpload: async (workspaceId, callbacks) => {
            const file = await BROWSER_FILE_UTILS.chooseFileInBrowser({shouldUseDirectory: callbacks.shouldUseDirectory});
            if (!file) {
                return null;
            }

            const actualUploadFile = async (args: {fileName: string; fileToUpload: File | Blob; lastModifiedAt: number | null; onUploadStart: typeof callbacks['onUploadStart']; onUploadProgress?: typeof callbacks['onUploadProgress']}) => {
                const fileSize = args.fileToUpload.size;
                const MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5MB

                args.onUploadStart(args.fileName);

                if (fileSize >= MULTIPART_THRESHOLD) {
                    try {
                        const fileMeta = await BROWSER_FILE_UTILS.uploadFileMultipart(args.fileName, args.fileToUpload, args.lastModifiedAt, workspaceId, actions, args.onUploadProgress);
                        return fileMeta;
                    } catch (e) {
                        console.error('Multipart upload failed, falling back to single upload:', e);
                    }
                }

                const response = await actions.createUploadUrl({workspaceId, fileName: args.fileName, contentType: args.fileToUpload.type});
                if (!response) {
                    return null;
                }

                if ('error' in response) {
                    console.error(response.error);
                    return null;
                }

                const token = localStorage.getItem('RN_STORED_TOKEN');
                if (token) {
                    response.url += `&auth_token=${token}`;
                }

                try {
                    const fileMeta = await BROWSER_FILE_UTILS.uploadFile(args.fileName, args.fileToUpload, args.lastModifiedAt, response.url);
                    return fileMeta;
                } catch (e) {
                    console.error(e);
                    return null;
                }
            };

            if (Array.isArray(file)) {
                const fileArray = Array.from(file);
                const firstFile = fileArray[0];
                const directoryPath = firstFile?.webkitRelativePath.substring(0, firstFile?.webkitRelativePath.indexOf('/'));
                const isDirectoryArgs = {
                    directoryPath,
                    computeHashes: async () => {
                        const hashes: {relativePath: string; hash: string; size: number}[] = [];
                        for (const f of fileArray) {
                            const {hash, size} = await browserComputeSha1(f);
                            if (f.size === 0) {
                                continue;
                            }
                            const pathWithoutFolderName = f.webkitRelativePath.substring(f.webkitRelativePath.indexOf('/') + 1);
                            hashes.push({relativePath: pathWithoutFolderName, hash, size});
                        }
                        return hashes;
                    },
                    uploadFiles: async (filePaths: string[], directoryCallbacks: DirectoryUploadCallbacks) => {
                        const fileMetas: StoredFileMetaWithDurationAndWaveformData[] = [];
                        for (const filePath of filePaths) {
                            const innerFile = fileArray.find(f => f.webkitRelativePath.substring(f.webkitRelativePath.indexOf('/') + 1) === filePath);
                            if (!innerFile) {
                                continue;
                            }
                            // directoryCallbacks.onUploadStart(filePath);
                            try {
                                const fileMeta = await actualUploadFile({fileName: filePath, fileToUpload: innerFile, lastModifiedAt: innerFile.lastModified, onUploadStart: directoryCallbacks.onUploadStart, onUploadProgress: directoryCallbacks.onUploadProgress});
                                if (!fileMeta) {
                                    continue;
                                }
                                fileMetas.push(fileMeta);
                            } catch (e) {
                                console.error(e);
                            } finally {
                                directoryCallbacks.onUploadEnd(filePath);
                            }
                        }
                        return fileMetas;
                    },
                };

                callbacks.onIsDirectory(isDirectoryArgs);

                return null;
            }

            if (file.name.endsWith('.zip')) {
                const directoryNameWithoutZip = file.name.replace('.zip', '');
                const zip = new JSZip();
                const blob = file;
                const zipObj = await zip.loadAsync(blob);

                const zipIsDirectoryArgs = {
                    directoryPath: directoryNameWithoutZip,
                    computeHashes: async () => {
                        const hashes: {relativePath: string; hash: string; size: number; lastModified: number}[] = [];
                        for (const [fileName, file] of Object.entries(zipObj.files)) {
                            const fileBlob = await file.async('blob');

                            const {hash, size} = await browserComputeSha1(fileBlob);
                            hashes.push({relativePath: fileName, hash, size, lastModified: file.date.getTime()});
                        }
                        return hashes;
                    },
                    uploadFiles: async (filePaths: string[], directoryCallbacks: DirectoryUploadCallbacks) => {
                        const fileMetas: StoredFileMetaWithDurationAndWaveformData[] = [];
                        for (const filePath of filePaths) {
                            const file = zipObj.files[filePath];
                            // directoryCallbacks.onUploadStart(filePath);
                            try {
                                const fileBlob = await file.async('blob');
                                const fileMeta = await actualUploadFile({fileName: filePath, fileToUpload: fileBlob, lastModifiedAt: file.date.getTime(), onUploadStart: directoryCallbacks.onUploadStart, onUploadProgress: directoryCallbacks.onUploadProgress});
                                if (fileMeta) {
                                    fileMetas.push(fileMeta);
                                }
                            } catch (e) {
                                console.error(e);
                            } finally {
                                directoryCallbacks.onUploadEnd(filePath);
                            }
                        }
                        return fileMetas;
                    },
                };

                callbacks.onIsDirectory(zipIsDirectoryArgs);

                return null;
            }

            // Check if single file is >20MB and trigger notification
            if (file.size > 20 * 1024 * 1024 && callbacks.onHasLargeFile) {
                callbacks.onHasLargeFile();
            }

            const fileMeta = await actualUploadFile({fileName: file.name, fileToUpload: file, lastModifiedAt: file.lastModified, onUploadStart: callbacks.onUploadStart, onUploadProgress: callbacks.onUploadProgress});
            return fileMeta;
        },
    };

    return {
        client: () => fileClient,
        createUploadUrl: async (args: {workspaceId: string, fileName: string, contentType: string}) => {
            return actions.createUploadUrl(args)
        },
        createMultipartUpload: async (args: {workspaceId: string, fileName: string, contentType: string}) => {
            return actions.createMultipartUpload(args)
        },
        createMultipartUploadPartUrl: async (args: {filePath: string, uploadId: string, partNumber: number, contentType: string}) => {
            return actions.createMultipartUploadPartUrl(args)
        },
        completeMultipartUpload: async (args: {filePath: string, uploadId: string, parts: Array<{ETag: string; PartNumber: number}>}) => {
            return actions.completeMultipartUpload(args)
        },
        abortMultipartUpload: async (args: {filePath: string, uploadId: string}) => {
            return actions.abortMultipartUpload(args)
        },
    };
});

// export class RemoteFileUploadClient implements FileClient {
//     public getFileSrc = async (fileInfo: DBObjects['File']) => {
//         return '';
//         // const FILE_SERVER_HOST = `${location.protocol}//${location.hostname}:6969`;
//         // return `${FILE_SERVER_HOST}/${fileInfo.full_path}`;
//     };

//     public chooseFileAndUpload = async (workspaceId: string) => {
//         const file = await BROWSER_FILE_UTILS.chooseFileInBrowser();
//         if (!file) {
//             return null;
//         }

//         return this.uploadFile(file, workspaceId);
//     };

//     public uploadFile = async (file: File, workspaceId: string): Promise<StoredFileMeta> => {
//         const uploadUrl = `/upload?workspace_id=${workspaceId}`;
//         return BROWSER_FILE_UTILS.uploadFile(file, uploadUrl);
//     };
// }

export const BROWSER_FILE_UTILS = {
    chooseFileInBrowser: (args: {shouldUseDirectory: boolean}) => {
        return new Promise<File | File[] | null>(r => {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.multiple = true;
            if (args.shouldUseDirectory) {
                fileInput.webkitdirectory = true;
            }

            if (process.env.IS_E2E) {
                fileInput.style.position = 'fixed';
                fileInput.style.top = '0';
                fileInput.style.left = '0';
                fileInput.style.opacity = '1';
                fileInput.style.zIndex = '2147483647'; // max z-index
                fileInput.style.pointerEvents = 'auto';
            } else {
                fileInput.style.display = 'none';
            }

            fileInput.oncancel = () => {
                r(null);
            };

            fileInput.onchange = (e) => {
                const target = e.target as HTMLInputElement;
                const files = target.files;
                document.body.removeChild(fileInput);

                if (!files?.length) {
                    r(null);
                    return;
                }

                const fileArray = Array.from(files);
                const filteredWithSize = fileArray.filter(f => f.size > 0);
                if (filteredWithSize.length === 1) {
                    r(filteredWithSize[0]);
                    return;
                }

                r(filteredWithSize);
            }

            document.body.appendChild(fileInput);

            if (!process.env.IS_E2E) {
                fileInput.click();
            }
        });
    },
    uploadFile: async (fileName: string, file: File | Blob, lastModifiedAt: number | null, uploadUrl: string): Promise<StoredFileMetaWithDurationAndWaveformData> => {
        try {
            const {hash, size} = await browserComputeSha1(file);
            if (size === 0) {
                throw new Error('File is empty');
            }

            const uploadPromise = fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': file.type || 'text/plain',
                    'Cache-Control': 'max-age=31536000, public',
                },
                body: file,
            });

            let duration: number | null = null;
            let waveform_data: string | null = null;

            if (isAudioFile(fileName)) {
                try {
                    const arrayBufferPromise = file.arrayBuffer();
                    const peaksAndDuration = await generatePeaksFromArrayBuffer(await arrayBufferPromise, 1000, (arrayBuffer) => {
                        const audioContext = new (globalThis.AudioContext || (globalThis as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext)();
                        return audioContext.decodeAudioData(arrayBuffer);
                    });

                    duration = peaksAndDuration.duration;
                    waveform_data = JSON.stringify(peaksAndDuration.peaks);
                } catch (e) {
                    console.error(e);
                }
            }

            const response = await uploadPromise;

            if (!response.ok) {
                throw new Error('Error uploading file');
            }

            const responseText = await response.text();

            let filePath = '';
            try {
                // TODO: remove this json parsing. we should just have the local dev server do uploads the same way with file paths. don't return an object with file path data, because that's not how it will go in production anyway
                filePath = JSON.parse(responseText).filePath;
            } catch (error) {
                if (uploadUrl.includes('/workspaces') && uploadUrl.includes('?')) {
                    const index = uploadUrl.indexOf('/workspaces');
                    const questionMarkIndex = uploadUrl.indexOf('?');
                    filePath = decodeURIComponent(uploadUrl.slice(index + 1, questionMarkIndex));
                }
            }

            if (!filePath) {
                throw new Error('No file path found in server response');
            }

            return {
                fullPath: filePath,
                name: fileName,
                mimeType: file.type,
                size,
                hash,
                lastModifiedAt: lastModifiedAt || undefined,
                duration,
                waveform_data,
            };
        } catch (error) {
            throw new Error('Error parsing server response: ' + error);
        }
    },
    uploadFileMultipart: async (fileName: string, file: File | Blob, lastModifiedAt: number | null, workspaceId: string, actions: any, onUploadProgress?: (fileName: string, progress: {uploaded: number; total: number}) => void): Promise<StoredFileMetaWithDurationAndWaveformData> => {
        const PART_SIZE = 5 * 1024 * 1024; // 5MB parts
        const fileSize = file.size;
        const numParts = Math.ceil(fileSize / PART_SIZE);

        try {
            const {hash, size} = await browserComputeSha1(file);
            if (size === 0) {
                throw new Error('File is empty');
            }

            // Initialize multipart upload
            const initResponse = await actions.createMultipartUpload({
                workspaceId,
                fileName,
                contentType: file.type
            });

            if ('error' in initResponse) {
                throw new Error(initResponse.error);
            }

            const { uploadId, filePath } = initResponse;
            const uploadedParts: Array<{ETag: string; PartNumber: number}> = [];

            try {
                // Upload parts with retry logic and progress reporting
                const MAX_CONCURRENT_UPLOADS = 3;
                const MAX_RETRIES = 3;
                let uploadedBytes = 0;
                
                const uploadPartWithRetry = async (partNumber: number, start: number, end: number): Promise<{ETag: string; PartNumber: number}> => {
                    const partData = file.slice(start, end);
                    let lastError: Error | null = null;
                    
                    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
                        try {
                            const partUrlResponse = await actions.createMultipartUploadPartUrl({
                                filePath,
                                uploadId,
                                partNumber,
                                contentType: file.type
                            });

                            if ('error' in partUrlResponse) {
                                throw new Error(`Failed to get part URL for part ${partNumber}: ${partUrlResponse.error}`);
                            }

                            const token = localStorage.getItem('RN_STORED_TOKEN');
                            let partUrl = partUrlResponse.url;
                            if (token) {
                                partUrl += `&auth_token=${token}`;
                            }

                            const response = await fetch(partUrl, {
                                method: 'PUT',
                                headers: {
                                    'Content-Type': file.type || 'application/octet-stream',
                                },
                                body: partData,
                            });

                            if (!response.ok) {
                                throw new Error(`Failed to upload part ${partNumber}: ${response.statusText}`);
                            }

                            const etag = response.headers.get('ETag');
                            if (!etag) {
                                throw new Error(`No ETag received for part ${partNumber}`);
                            }

                            // Update progress when part succeeds
                            uploadedBytes += partData.size;
                            onUploadProgress?.(fileName, { uploaded: uploadedBytes, total: fileSize });

                            return { ETag: etag, PartNumber: partNumber };
                        } catch (error) {
                            lastError = error as Error;
                            if (attempt < MAX_RETRIES) {
                                console.warn(`Part ${partNumber} failed, retrying (attempt ${attempt + 1}/${MAX_RETRIES}):`, error);
                                // Wait before retry with exponential backoff
                                await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
                            }
                        }
                    }
                    
                    throw new Error(`Failed to upload part ${partNumber} after ${MAX_RETRIES} attempts: ${lastError?.message}`);
                };

                // Upload parts with controlled concurrency
                for (let i = 0; i < numParts; i += MAX_CONCURRENT_UPLOADS) {
                    const batch = [];
                    for (let j = 0; j < MAX_CONCURRENT_UPLOADS && i + j < numParts; j++) {
                        const partNumber = i + j + 1; // Part numbers start at 1
                        const start = (partNumber - 1) * PART_SIZE;
                        const end = Math.min(start + PART_SIZE, fileSize);
                        batch.push(uploadPartWithRetry(partNumber, start, end));
                    }
                    
                    const batchResults = await Promise.all(batch);
                    uploadedParts.push(...batchResults);
                }

                // Complete multipart upload
                const completeResponse = await actions.completeMultipartUpload({
                    filePath,
                    uploadId,
                    parts: uploadedParts
                });

                if ('error' in completeResponse) {
                    throw new Error(`Failed to complete multipart upload: ${completeResponse.error}`);
                }

            } catch (error) {
                // If upload fails, abort the multipart upload
                try {
                    await actions.abortMultipartUpload({ filePath, uploadId });
                } catch (abortError) {
                    console.error('Failed to abort multipart upload:', abortError);
                }
                throw error;
            }

            // Generate waveform data if it's an audio file
            let duration: number | null = null;
            let waveform_data: string | null = null;

            if (isAudioFile(fileName)) {
                try {
                    const arrayBufferPromise = file.arrayBuffer();
                    const peaksAndDuration = await generatePeaksFromArrayBuffer(await arrayBufferPromise, 1000, (arrayBuffer) => {
                        const audioContext = new (globalThis.AudioContext || (globalThis as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext)();
                        return audioContext.decodeAudioData(arrayBuffer);
                    });

                    duration = peaksAndDuration.duration;
                    waveform_data = JSON.stringify(peaksAndDuration.peaks);
                } catch (e) {
                    console.error('Failed to generate waveform data:', e);
                }
            }

            return {
                fullPath: filePath,
                name: fileName,
                mimeType: file.type,
                size,
                hash,
                lastModifiedAt: lastModifiedAt || undefined,
                duration,
                waveform_data,
            };

        } catch (error) {
            throw new Error('Multipart upload failed: ' + error);
        }
    },
};
